import type { TFunction } from 'i18next';
import type { NameMatch } from './matchOptionByName';
import type { NoteTaskProposal } from '../types';

interface AssigneeHintArgs {
  t: TFunction;
  proposal: NoteTaskProposal | null;
  projectId: string;
  memberOptionsCount: number;
  assigneeMatch: NameMatch;
  orgMembers: { displayName: string }[];
}

/**
 * Explains what happened to the assignee name the note mentioned, in the user's terms.
 *
 * Assignable people are the project's members and only those — a task owned by
 * someone without access to the project is a task nobody can open. So when the
 * named person is not one of them, the field stays empty and this says why,
 * as information about the note and the project, never as a mistake the user made.
 */
export function assigneeHint({
  t,
  proposal,
  projectId,
  memberOptionsCount,
  assigneeMatch,
  orgMembers,
}: AssigneeHintArgs): string | undefined {
  if (proposal?.assigneeIsCaller) {
    return t('convertToTask.observations.assigneeIsCaller');
  }

  const name = proposal?.assigneeName;
  if (!name) return undefined;

  // No project yet: nothing failed, the list simply does not exist yet.
  if (!projectId) {
    return t('convertToTask.observations.assigneePendingProject', { name });
  }

  // Members still loading — stay quiet rather than claim they are missing.
  if (memberOptionsCount === 0) return undefined;

  if (assigneeMatch.kind === 'matched') return undefined;

  if (assigneeMatch.kind === 'ambiguous') {
    return t('convertToTask.observations.nameAmbiguous', {
      name,
      options: assigneeMatch.labels.join(', '),
    });
  }

  const inOrganization = orgMembers.some((m) => m.displayName.toLowerCase() === name.toLowerCase());

  return inOrganization
    ? t('convertToTask.observations.assigneeInOrgNotProject', { name })
    : t('convertToTask.observations.assigneeNotFound', { name });
}

/**
 * Explains a name the note mentioned that did not end up selected: several options
 * fit, or none did. Silent while the list is still loading — an empty list is not
 * evidence that the name is wrong.
 */
export function nameHint(
  t: TFunction,
  match: NameMatch,
  name: string | null | undefined,
  optionCount: number,
  notFoundKey: string,
): string | undefined {
  if (!name || match.kind === 'matched' || optionCount === 0) return undefined;

  if (match.kind === 'ambiguous') {
    return t('convertToTask.observations.nameAmbiguous', {
      name,
      options: match.labels.join(', '),
    });
  }

  return t(notFoundKey, { name });
}

/** The note stated a date in wording the API could not turn into a day. */
export function dateHint(
  t: TFunction,
  expression: string | null | undefined,
  resolved: string | null | undefined,
): string | undefined {
  if (!expression || resolved) return undefined;
  return t('convertToTask.observations.dateNotResolved', { expression });
}
